/// <amd-module name="Wit/Identity/Utilities/WorkItemIdentityHelper" />
import * as Utils_String from "VSS/Core/Util/String";
import { localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { IIdentity } from "VSS/Features/Identities/IdentityService";
import { IVssPageContext } from "VSS/Platform/Context";
import { IdentityRef } from "VSS/Platform/Contracts/WebApi";
import { getIdentityImageUrl, IVssIdentityService } from "VSS/Platform/Identity";
import { getAssetLocation } from "VSS/Platform/Location";
import { WorkItemIdentityRef } from "Wit/Identity/Interfaces/WorkItemInterfaces";
import { SubjectType } from "../Constants/Constants";

let _referencedIdentitiesByDistinctDisplayName: { [key: string]: WorkItemIdentityRef } = {};
let _referencedIdentitiesByDescriptor: { [key: string]: WorkItemIdentityRef } = {};
let _isIdentityRefResolvedByDescriptor: { [descriptor: string]: boolean } = {};

export interface IIdentityReference {
    id: string;
    displayName: string;
    uniqueName: string;
    isContainer?: boolean;
    isTfsGroup?: boolean;
    // whether or not to show generic image in identity controls if its an unresolved identity
    showGenericImage?: boolean;
    // whether or not the identity is an AAD backed identity or not
    isAadIdentity?: boolean;
}

export var IDENTITY_UNIQUEFIEDNAME_SEPERATOR_START = "<";
export var IDENTITY_UNIQUEFIEDNAME_SEPERATOR_END = ">";
export var AAD_IDENTITY_UNIQUEFIEDNAME_SEPERATOR_START = "<<";
export var AAD_IDENTITY_UNIQUEFIEDNAME_SEPERATOR_END = ">>";
export var TFS_GROUP_PREFIX = "id:";
export var AAD_IDENTITY_USER_PREFIX = "user:";
export var AAD_IDENTITY_GROUP_PREFIX = "group:";
export var IDENTITY_UNIQUENAME_SEPARATOR = "\\";
export var DESCRIPTOR_PREFIX = "desc:";

function getDescriptorFromUniqueName(uniqueName: string): string {
    if (!uniqueName) {
        return "";
    }

    if (uniqueName.indexOf(DESCRIPTOR_PREFIX) != 0) {
        return "";
    }

    return uniqueName.replace(/desc:/i, "");
}

// Given a group uniquename that looks like id:2465ce16-6260-47a2-bdff-5fe4bc912c04\Build Administrators or id:2465ce16-6260-47a2-bdff-5fe4bc912c04, it will get the tfid from unique name
export function getVsIdFromGroupUniqueName(str: string): string {
    var leftPart: string;
    if (!str) {
        return "";
    }

    var vsid = "";
    var i = str.lastIndexOf(IDENTITY_UNIQUENAME_SEPARATOR);
    if (i === -1) {
        leftPart = str;
    } else {
        leftPart = str.substr(0, i);
    }

    if (Utils_String.startsWith(leftPart, TFS_GROUP_PREFIX)) {
        var rightPart = leftPart.substr(3).trim();
        vsid = Utils_String.isGuid(rightPart) ? rightPart : "";
    }

    return vsid;
}

/**
 * Parse a distinct display name string into an identity reference object
 *
 * @param name A distinct display name for an identity
 */
export function parseUniquefiedIdentityName(origName: string): IIdentityReference {
    if (!origName) {
        return null;
    }

    var i = origName.lastIndexOf(AAD_IDENTITY_UNIQUEFIEDNAME_SEPERATOR_START);
    var j = origName.lastIndexOf(AAD_IDENTITY_UNIQUEFIEDNAME_SEPERATOR_END);
    var isContainer: boolean = false;
    var isTfsGroup: boolean = false;
    var isAad: boolean = false;
    var name = origName;
    if (i >= 0 && j > i) {
        isAad = true;
        // replace "<<" with "<" and ">>" with ">" in case of an AAD identity string representation to make further processing easier
        name = name
            .replace(AAD_IDENTITY_UNIQUEFIEDNAME_SEPERATOR_START, IDENTITY_UNIQUEFIEDNAME_SEPERATOR_START)
            .replace(AAD_IDENTITY_UNIQUEFIEDNAME_SEPERATOR_END, IDENTITY_UNIQUEFIEDNAME_SEPERATOR_END);
    }

    i = name.lastIndexOf(IDENTITY_UNIQUEFIEDNAME_SEPERATOR_START);
    j = name.lastIndexOf(IDENTITY_UNIQUEFIEDNAME_SEPERATOR_END);
    var displayName = name;
    var alias = "";
    var rightPart = "";
    let descriptor = "";
    var id = "";
    if (i >= 0 && j > i) {
        displayName = name.substr(0, i).trim();
        if (isAad) {
            // if its an AAD identity, the string would be in format - name <<object id>>
            id = name.substr(i + 1, j - i - 1).trim(); // this would be in format objectid\email

            if (id.indexOf(AAD_IDENTITY_USER_PREFIX) === 0) {
                id = id.substr(AAD_IDENTITY_USER_PREFIX.length);
            } else if (id.indexOf(AAD_IDENTITY_GROUP_PREFIX) === 0) {
                isContainer = true;
                id = id.substr(AAD_IDENTITY_GROUP_PREFIX.length);
            }

            var ii = id.lastIndexOf("\\");
            if (ii > 0) {
                rightPart = id.substr(ii + 1).trim();
                if (rightPart.indexOf("@") !== -1) {
                    // if its a valid alias
                    alias = rightPart;
                    id = id.substr(0, ii).trim();
                } else {
                    // if its not a valid alias, treat it as a non-identity string
                    displayName = origName;
                    id = "";
                    isAad = false;
                    isContainer = false;
                }
            }
        } else {
            rightPart = name.substr(i + 1, j - i - 1).trim(); //gets string in the <>
            var vsIdFromAlias: string = getVsIdFromGroupUniqueName(rightPart); // if it has vsid in unique name (for TFS groups)
            descriptor = getDescriptorFromUniqueName(rightPart);
            if (
                rightPart.indexOf("@") !== -1 ||
                rightPart.indexOf("\\") !== -1 ||
                rightPart.indexOf(DESCRIPTOR_PREFIX) !== -1 ||
                vsIdFromAlias ||
                Utils_String.isGuid(rightPart)
            ) {
                // if its a valid alias
                alias = rightPart;

                // If the alias component is just a guid then this is not a uniqueName but
                // vsId which is used only for TFS groups
                if (vsIdFromAlias != "") {
                    id = vsIdFromAlias;
                    isContainer = true;
                    isTfsGroup = true;
                    alias = "";
                }
            } else {
                // if its not a valid alias, treat it as a non-identity string
                displayName = origName;
            }
        }
    }

    return {
        id: id ?? displayName,
        displayName: displayName,
        uniqueName: descriptor || alias || displayName,
        isAadIdentity: isAad,
        isContainer: isContainer,
        isTfsGroup: isTfsGroup
    };
}

/**
 * If the value is a WorkItemIdentityRef, add it to the resolved cache.  This ensures
 * that any call to resolveIdentityRefToWorkItemIdentityRef will not hit the graph
 * service for this identity.
 */
export function setResolvedByDescriptorIfWorkItemIdentityRef(value: any): void {
    if (isWorkItemIdentityRef(value) && value.identityRef && value.identityRef.descriptor) {
        _isIdentityRefResolvedByDescriptor[value.identityRef.descriptor] = true;
    }
}

/**
 * If the value is a WorkItemIdentityRef and the consumer isn't explicitly asking
 * for it, return the distinctDisplayName.
 *
 * If it's not a WorkItemIdentityRef, and they explicitly are asking for it, attempt
 * to convert the value to an IdentityRef by parsing the details from the combo string.
 *
 * Otherwise just return the original value.
 */
export function convertPotentialIdentityRefFromFieldValue(pageContext: IVssPageContext, value: any, asIdentityRef?: boolean): any {
    const valueIsWorkItemIdentityRef = isWorkItemIdentityRef(value);
    const valueIsIdentityRef = isIdentityRef(value);
    if (asIdentityRef && !valueIsWorkItemIdentityRef && !valueIsIdentityRef) {
        return createIdentityRefFromDistinctDisplayName(pageContext, value);
    } else if (valueIsWorkItemIdentityRef) {
        const workItemIdentityRef = <WorkItemIdentityRef>value;
        return asIdentityRef
            ? workItemIdentityRef.identityRef
            : workItemIdentityRef.distinctDisplayName || workItemIdentityRef.identityRef.displayName;
    } else if (valueIsIdentityRef) {
        const identityRef = <IdentityRef>value;
        identityRef.uniqueName = identityRef.uniqueName ?? identityRef.displayName;
        return asIdentityRef ? identityRef : identityRef.displayName;
    }

    return value;
}

/**
 * A smaller, more lightweight version of the function above to be used if you know the value you want back
 * is not an Identity ref
 *
 * The main purpose of this function is to do this conversion without requiring pageContext
 */
export function convertPotentialIdentityRefFromFieldValueNotAsIdentityRef(value: unknown) {
    if (isWorkItemIdentityRef(value)) {
        return (value as WorkItemIdentityRef).distinctDisplayName || (value as WorkItemIdentityRef).identityRef.displayName;
    } else if (isIdentityRef(value)) {
        return (value as IdentityRef).displayName;
    }

    return value;
}

/**
 * Returns true if the provided value is not null and a WorkItemIdentityRef
 */
export function isWorkItemIdentityRef(value: any): value is WorkItemIdentityRef {
    if (value && value.identityRef !== undefined) {
        const workItemIdentityRef = <WorkItemIdentityRef>value;
        // cache the work item identity ref by distinct display name
        if (workItemIdentityRef.distinctDisplayName && !_referencedIdentitiesByDistinctDisplayName[workItemIdentityRef.distinctDisplayName]) {
            if(workItemIdentityRef.identityRef && !workItemIdentityRef.identityRef.uniqueName) {
                workItemIdentityRef.identityRef.uniqueName = workItemIdentityRef.identityRef.displayName;
            }
            _referencedIdentitiesByDistinctDisplayName[workItemIdentityRef.distinctDisplayName] = workItemIdentityRef;
        }

        // cache the work item identity ref by descriptor
        if (
            workItemIdentityRef.identityRef &&
            workItemIdentityRef.identityRef.descriptor &&
            !_referencedIdentitiesByDescriptor[workItemIdentityRef.identityRef.descriptor]
        ) {
            _referencedIdentitiesByDescriptor[workItemIdentityRef.identityRef.descriptor] = workItemIdentityRef;
        }

        return true;
    } else {
        return false;
    }
}

/**
 * Returns true if the provided value is not null and an IdentityRef
 */
export function isIdentityRef(value: any): value is IdentityRef {
    return !!(value && value.displayName !== undefined);
}

/**
 * Constructs an IdentityRef from distinct display name.
 *
 * This is required to handle the scenario where the IdentityRef feature flag is off.
 * @param pageContext
 * @param value
 * @param imageUrl set the IdentityRef's image to this. If not provided, no image will be set if the identity isn't already cached.
 */
export function createIdentityRefFromDistinctDisplayName(pageContext: IVssPageContext, value: string, imageUrl?: string): IdentityRef {
    if (_referencedIdentitiesByDistinctDisplayName[value]) {
        return _referencedIdentitiesByDistinctDisplayName[value].identityRef;
    }

    const parsedIdentity = parseUniquefiedIdentityName(value);
    const identityService = pageContext.getService<IVssIdentityService>("IVssIdentityService");
    imageUrl = imageUrl || identityService.getIdentityImageUrl(parsedIdentity ? parsedIdentity.id : "");

    // aad identities cannot be resolved by the graph APIs, so keep as a non-identity constant
    if (parsedIdentity && parsedIdentity.isAadIdentity) {
        return {
            displayName: value
        } as IdentityRef;
    }

    return (
        parsedIdentity &&
        ({
            displayName: parsedIdentity.displayName,
            uniqueName: parsedIdentity.uniqueName,
            imageUrl: imageUrl,
            _links: {
                avatar: {
                    href: imageUrl
                }
            }
        } as IdentityRef)
    );
}

/**
 * If the value is a WorkItemIdentityRef, return as is.
 *
 * If it's not a WorkItemIdentityRef and is a string of the format "DisplayName <uniqueName>",
 * attempt to convert the value to  WorkItemIdentityRef by parsing the details from the combo string.
 * into an IdentityRef and keeping the value as the distinctDisplayName.
 *
 * Is for some reason, parseUniquefiedIdentityName returns null, we fallback to a dummy
 * IdentityRef with displayName same as the value.
 */
export function convertWorkItemIdentityRefFromFieldValue(
    pageContext: IVssPageContext,
    value: string | WorkItemIdentityRef | undefined | null | IIdentity
): WorkItemIdentityRef {
    if (isWorkItemIdentityRef(value)) {
        return sanitizeWorkItemIdentityRef(value);
    }

    const isIIdentityCheck = isIIdentity(value);
    if (isIIdentityCheck) {
        const iidentity = value as IIdentity;
        const result = convertIIdentityToWorkItemIdentityRef(pageContext, iidentity, iidentity.image);
        return sanitizeWorkItemIdentityRef(result);
    }

    let identityRef = convertPotentialIdentityRefFromFieldValue(pageContext, value, true);
    if (!identityRef) {
        // In the unlikely event of parseUniquefiedIdentityName failing to parse this identity,
        // fall back to a dummy IdentityRef to let consumers of this method always assume that it won't be null.
        identityRef = {
            displayName: value
        } as IdentityRef;
    }

    return sanitizeWorkItemIdentityRef({
        distinctDisplayName: identityRef.displayName ?? value,
        identityRef
    });
}

function isIIdentity(value: any): value is IIdentity {
    const result = value && typeof value.displayName === 'string' && value.entityId !== undefined;
    return result;
}

function sanitizeWorkItemIdentityRef(value: WorkItemIdentityRef): WorkItemIdentityRef {
    let workItemIdentityRef = { ...value };
    if (value.identityRef.displayName) {
        workItemIdentityRef.identityRef.displayName = parseUniquefiedIdentityName(workItemIdentityRef.identityRef.displayName).displayName;
    }
    return workItemIdentityRef;
}

/**
 * Removes duplicates from an array of WorkItemIdentityRef objects, treating distinctDisplayName as the key
 * and then sorts it by distinctDisplayName using localeIgnoreCaseComparer.
 * @param workitemIdentityRefs
 */
export function uniqueSortWorkItemIdentityRefObjects(workitemIdentityRefs: WorkItemIdentityRef[]): WorkItemIdentityRef[] {
    if (!workitemIdentityRefs || workitemIdentityRefs.length === 0) {
        return workitemIdentityRefs;
    }

    const uniqueIdentities: { [key: string]: WorkItemIdentityRef } = {};
    workitemIdentityRefs.forEach(w => (uniqueIdentities[w.distinctDisplayName] = w));
    const sortedWorkitemIdentityRefs = Object.keys(uniqueIdentities).sort(Utils_String.localeIgnoreCaseComparer);

    return sortedWorkitemIdentityRefs.map(s => uniqueIdentities[s]);
}

/**
 * Returns a user friendly distinct display name string for an identity reference object
 *
 * @param identity An identity
 */
export function getFriendlyDistinctDisplayName(identity: IIdentityReference): string {
    if (identity.displayName && identity.uniqueName && !identity.isTfsGroup) {
        if (identity.isContainer) {
            return getDistinctGroupDisplayName(identity.displayName, identity.id);
        }
        return getDistinctDisplayName(identity.displayName, identity.uniqueName);
    } else {
        return identity.displayName || "";
    }
}

/**
 * Returns a user  distinct display name string for an passed displayname and unique name
 *
 * @param displayName an user's displayname
 * @param uniqueName user's alias or domain\\alias
 */
export function getDistinctDisplayName(displayName: string, uniqueName: string): string {
    if (displayName && uniqueName) {
        return Utils_String.format(
            "{0} {1}{2}{3}",
            displayName,
            IDENTITY_UNIQUEFIEDNAME_SEPERATOR_START,
            uniqueName,
            IDENTITY_UNIQUEFIEDNAME_SEPERATOR_END
        );
    }

    return "";
}

/**
 * Returns a group distinct display name string for an passed displayname and id
 *
 * @param displayName an user's displayname
 * @param id group's id
 */
export function getDistinctGroupDisplayName(displayName: string, id: string): string {
    if (displayName && id) {
        return Utils_String.format(
            "{0} {1}{2}{3}{4}",
            displayName,
            IDENTITY_UNIQUEFIEDNAME_SEPERATOR_START,
            TFS_GROUP_PREFIX,
            id,
            IDENTITY_UNIQUEFIEDNAME_SEPERATOR_END
        );
    }

    return "";
}

/**
 * Returns a distinct display name string representation for an identity reference object recognizable by WIT
 * The AAD identity string representation we use for the control is -  name <<objectId\email>>
 *
 * @param identity An identity
 */
export function getUniquefiedIdentityName(identity: IIdentity): string {
    if (!identity) {
        return "";
    }

    /**
     * It is preferable to calculate uniquename based on if it's hosted which is more reliable and
     * for hosted we should not really use samAccoutName(but also it's expected to be null).
     * There's a IIdentity.isHosted but we cannot guarantee it always present or we'll have to pass-in a parameter for isHosted
     * also causing a large downstream change.
     * So after all the consideration we decided to rely on these 3 parameters.
     * IMPORTANT! The order of parameters matters at least for onprem - we should rely on samAccountName more than on identity.mail
     */
    const uniqueName = identity.signInAddress || identity.samAccountName || identity.mail || identity.originId;

    if (isAADIdentity(identity)) {
        const isAadGroup = Utils_String.equals(identity.entityType, "Group", true);
        const prefix = isAadGroup ? AAD_IDENTITY_GROUP_PREFIX : AAD_IDENTITY_USER_PREFIX;
        if ((identity.signInAddress || identity.mail) && !isAadGroup) {
            return Utils_String.format(
                "{0} {1}{2}{3}\\{4}{5}",
                identity.displayName,
                AAD_IDENTITY_UNIQUEFIEDNAME_SEPERATOR_START,
                prefix,
                identity.originId,
                identity.signInAddress || identity.mail,
                AAD_IDENTITY_UNIQUEFIEDNAME_SEPERATOR_END
            );
        } else {
            return Utils_String.format(
                "{0} {1}{2}{3}{4}",
                identity.displayName,
                AAD_IDENTITY_UNIQUEFIEDNAME_SEPERATOR_START,
                prefix,
                identity.originId,
                AAD_IDENTITY_UNIQUEFIEDNAME_SEPERATOR_END
            );
        }
    } else if (isTfsGroup(identity)) {
        if (identity.localId) {
            return Utils_String.format(
                "{0} {1}{2}{3}{4}",
                identity.displayName,
                IDENTITY_UNIQUEFIEDNAME_SEPERATOR_START,
                TFS_GROUP_PREFIX,
                identity.localId.toUpperCase(),
                IDENTITY_UNIQUEFIEDNAME_SEPERATOR_END
            );
        } else {
            return identity.displayName;
        }
    } else if (uniqueName) {
        if (uniqueName.indexOf("@") === -1 && identity.scopeName && !isServicePrincipal(identity)) {
            // if uniqueName is not an email and identity is not a service principal, use both domain and alias
            return Utils_String.format(
                "{0} {1}{2}\\{3}{4}",
                identity.displayName,
                IDENTITY_UNIQUEFIEDNAME_SEPERATOR_START,
                identity.scopeName,
                uniqueName,
                IDENTITY_UNIQUEFIEDNAME_SEPERATOR_END
            );
        } else {
            // if uniqueName is an email or identity is a service principal, use email/guid for service principals
            return Utils_String.format(
                "{0} {1}{2}{3}",
                identity.displayName,
                IDENTITY_UNIQUEFIEDNAME_SEPERATOR_START,
                uniqueName,
                IDENTITY_UNIQUEFIEDNAME_SEPERATOR_END
            );
        }
    } else if (identity.subjectDescriptor) {
        return Utils_String.format(
            "{0} {1}{2}{3}{4}",
            identity.displayName,
            IDENTITY_UNIQUEFIEDNAME_SEPERATOR_START,
            DESCRIPTOR_PREFIX,
            identity.subjectDescriptor,
            IDENTITY_UNIQUEFIEDNAME_SEPERATOR_END
        );
    } else {
        return identity.displayName || "";
    }
}

/**
 * Convert an IIdentity to a WorkItemIdentityRef
 * @param pageContext
 * @param identity
 * @param imageUrl set the WorkItemIdentityRef's image to this. If not provided, no image will be set if the identity isn't already cached.
 * @returns
 */
export function convertIIdentityToWorkItemIdentityRef(pageContext: IVssPageContext, identity: IIdentity, imageUrl?: string): WorkItemIdentityRef {
    const distinctDisplayName = getUniquefiedIdentityName(identity);
    const identityRef = createIdentityRefFromDistinctDisplayName(pageContext, distinctDisplayName, imageUrl);
    if (identityRef.id === undefined && identity?.localId !== undefined) {
        identityRef.id = identity.localId;
    }
    if (identityRef.descriptor === undefined && identity?.subjectDescriptor !== undefined) {
        identityRef.descriptor = identity.subjectDescriptor;
    }

    return {
        distinctDisplayName,
        identityRef: identityRef
    };
}

export function convertWorkItemIdentityRefToIIdentity(
    pageContext: IVssPageContext,
    workItemIdentityRef: WorkItemIdentityRef | undefined
): IIdentity | undefined {
    const identityRef = workItemIdentityRef?.identityRef;
    if (!identityRef) {
        return;
    }

    return convertIdentityRefToIIdentity(pageContext, identityRef);
}

export function convertIdentityRefToIIdentity(pageContext: IVssPageContext, identityRef: IdentityRef): IIdentity {
    const identityService = pageContext.getService<IVssIdentityService>("IVssIdentityService");
    const imageUrl = identityService.getIdentityImageUrl(identityRef);
    return {
        displayName: identityRef.displayName,
        entityId: identityRef.id,
        entityType: "User",
        image: imageUrl
    } as IIdentity;
}

export function getIIdentityObject(pageContext: IVssPageContext, value: string, entityId?: string): IIdentity | undefined {
    if (!value) {
        return;
    }

    const macroMe = "@me";
    if (localeIgnoreCaseComparer(value, macroMe) === 0) {
        return {
            displayName: macroMe,
            entityId: macroMe,
            entityType: "User",
            originDirectory: "",
            originId: "",
            image: getAssetLocation(pageContext, "ms.vss-work-web/common-content/Content/memacro-user.svg")
        } as IIdentity;
    }
    const displayName: string = value.substring(0, value.indexOf("<")).trim();
    const isGroup: boolean = value.indexOf("<id:") !== -1;
    let id: string;
    if (isGroup) {
        id = value.substring(value.indexOf(":") + 1, value.length - 1).trim();
    } else {
        id = value.substring(value.indexOf("<") + 1, value.length - 1).trim();
    }
    const imageUrl = getIdentityImageUrl(pageContext, createIdentityRefFromDistinctDisplayName(pageContext, value));
    return {
        displayName: displayName,
        entityId: entityId ?? "",
        entityType: isGroup ? "Group" : "User",
        localId: isGroup ? id : "",
        mail: !isGroup ? id : "",
        originId: isGroup ? id : "",
        samAccountName: !isGroup ? id : "",
        signInAddress: !isGroup ? id : "",
        image: imageUrl
    } as IIdentity;
}

function isAADIdentity(entity: IIdentity): boolean {
    return Utils_String.equals(entity.originDirectory, "aad", true) && entity.originId && !entity.localId;
}

function isServicePrincipal(entity: IIdentity): boolean {
    return entity.subjectDescriptor.startsWith(SubjectType.AadServicePrincipal);
}

function isTfsGroup(entity: IIdentity): boolean {
    const indexOfGroupSeperator = entity.displayName.indexOf("\\");
    return (
        Utils_String.equals(entity.entityType, "Group", true) &&
        Utils_String.startsWith(entity.displayName, "[") &&
        indexOfGroupSeperator !== -1 &&
        Utils_String.endsWith(entity.displayName.substring(0, indexOfGroupSeperator), "]")
    );
}

const displayNameWithIdPattern = /.*\<[0-9\-\@a-f]+\>$/i;

/**
 * Sometimes, we get display names that look like this:
 *
 *     Microsoft.VisualStudio.Services.TFS <00000002-0000-8888-8000-000000000000@2c895908-04e0-4952-89fd-54b0046d6288>
 *
 * In this case, we should display something like "Microsoft.VisualStudio.Services.TFS"
 * @param rawDisplayName
 */
export function stripIdPartFromDisplayName(rawDisplayName: string): string {
    if (!displayNameWithIdPattern.test(rawDisplayName)) {
        return rawDisplayName;
    }

    return rawDisplayName.substr(0, rawDisplayName.lastIndexOf("<")).trim();
}

export function isAADGroupIdentity(identity: IIdentity | undefined): boolean {
    if (!identity) {
        return false;
    }

    return isAADIdentity(identity) && Utils_String.equals(identity.entityType, "Group", true);
}

export function getAADGroupName(identity: IIdentity): string {
    if (!identity.displayName || !identity.originId) {
        return "";
    }

    return Utils_String.format(
        "{0} {1}{2}{3}{4}",
        identity.displayName,
        AAD_IDENTITY_UNIQUEFIEDNAME_SEPERATOR_START,
        AAD_IDENTITY_GROUP_PREFIX,
        identity.originId,
        AAD_IDENTITY_UNIQUEFIEDNAME_SEPERATOR_END
    );
}
